import React, { useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import './Toast.css';

const Toast = ({ message, type = 'success', duration = 3000, onClose }) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => {
        if (onClose) onClose();
      }, 300); // Allow time for fade out animation
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  return isVisible ? (
    <div className={`custom-toast ${type} ${isVisible ? 'show' : 'hide'}`}>
      <div className="toast-content">
        {type === 'success' && (
          <svg className="toast-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
        {type === 'error' && (
          <svg className="toast-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
        <span>{message}</span>
      </div>
    </div>
  ) : null;
};

// Keep track of active toasts separately for each type
let errorToastCount = 0;
let successToastCount = 0;
const MAX_TOASTS = 3;
const TOAST_SPACING = 60;

export const showToast = (message, type = 'success', duration = 3000) => {
  if (!message) return;
  
  // Get or create container based on toast type
  const containerId = type === 'error' ? 'toast-container-error' : 'toast-container-success';
  let container = document.getElementById(containerId);
  
  if (!container) {
    container = document.createElement('div');
    container.id = containerId;
    // Always use top container for both types
    container.className = 'toast-container-top';
    document.body.appendChild(container);
  }

  // Check toast count based on type
  const currentCount = type === 'error' ? errorToastCount : successToastCount;
  if (currentCount >= MAX_TOASTS) return;

  const toastElement = document.createElement('div');
  
  // Always position from top for both types
  if (type === 'error') {
    toastElement.style.top = `${errorToastCount * TOAST_SPACING}px`;
    errorToastCount++;
  } else {
    toastElement.style.top = `${successToastCount * TOAST_SPACING}px`;
    successToastCount++;
  }
  
  container.appendChild(toastElement);
  const root = createRoot(toastElement);

  const removeToast = () => {
    if (toastElement) {
      if (type === 'error') {
        errorToastCount--;
      } else {
        successToastCount--;
      }
      
      root.unmount();
      container?.removeChild(toastElement);
      
      // Adjust positions of remaining toasts
      const toasts = container.children;
      Array.from(toasts).forEach((toast, index) => {
        toast.style.top = `${index * TOAST_SPACING}px`;
      });
    }
  };

  root.render(
    <Toast 
      message={message} 
      type={type} 
      duration={duration} 
      onClose={removeToast}
    />
  );
};

export default Toast; 